import { motion } from "framer-motion";

/**
 * Maps a pack.vibe.animation key to framer-motion props.
 * Returns { animate, transition } you can spread onto a motion element.
 * Falls back to a gentle bounce.
 */
export function vibeAnimationProps(animationKey) {
  switch (animationKey) {
    case "bounce":
      return {
        animate: { y: [0, -3, 0] },
        transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
      };
    case "flicker":
      // Candle flicker: subtle scale + rotation, irregular timing
      return {
        animate: { scale: [1, 1.06, 0.98, 1.04, 1], rotate: [0, 2, -1, 1, 0] },
        transition: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
      };
    case "drift":
      // Highway headlight drift: slow horizontal sway
      return {
        animate: { x: [-2, 2, -2] },
        transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" },
      };
    case "pulse-soft":
      return {
        animate: { scale: [1, 1.04, 1] },
        transition: { duration: 2.8, repeat: Infinity, ease: "easeInOut" },
      };
    case "glow":
      return {
        animate: { filter: ["brightness(1)", "brightness(1.25)", "brightness(1)"] },
        transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
      };
    case "flame":
      return {
        animate: { rotate: [0, 8, -6, 0], scale: [1, 1.08, 1] },
        transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
      };
    case "shake":
      return {
        animate: { rotate: [0, -2, 2, -1, 0] },
        transition: { duration: 0.5, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" },
      };
    case "wobble":
      return {
        animate: { rotate: [0, 12, -10, 8, -6, 0], y: [0, -2, 0] },
        transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
      };
    default:
      return {
        animate: { y: [0, -2, 0] },
        transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
      };
  }
}

/**
 * <VibeEmoji /> — emoji span animated according to pack.vibe.animation.
 */
export default function VibeEmoji({ emoji, animationKey, size = "text-3xl", glow }) {
  const props = vibeAnimationProps(animationKey);
  return (
    <motion.span
      {...props}
      className={`${size} block`}
      style={glow ? { filter: `drop-shadow(0 0 14px ${glow}99)` } : undefined}
    >
      {emoji}
    </motion.span>
  );
}
